import { OneBlinkFormControlled } from '@oneblink/apps-react'
import {
  ExecutedLookups,
  SetFormSubmission,
} from '@oneblink/apps-react/dist/types/form'
import { submissionService } from '@oneblink/apps-react'
import { FormTypes } from '@oneblink/types'
import styled from 'styled-components'

import Dialog, { DialogHeading, DialogHeadingText, CloseIcon } from './Dialog'
import LoadingSpinner from './LoadingSpinner'
import ErrorMessage from './ErrorMessage'

import config from '../config'
import { useAppConfig } from '../providers/AppConfigProvider'
import useGoogleAnalyticsFormBeforeUnload from '../hooks/useGoogleAnalyticsFormBeforeUnload'
import PaymentDialog from '../scenes/Payment/PaymentDialog'

const DraftTitleInput = styled.input`
  margin-bottom: 2rem;
`

DraftTitleInput.displayName = 'DraftTileInput'

const ActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.unit}px;
`

ActionsContainer.displayName = 'ActionsContainer'

const ServerValidationMessage = styled.span`
  white-space: pre-line;
`

type Props = {
  definition: FormTypes.Form
  submission: Record<string, unknown>
  setFormSubmission: SetFormSubmission
  onCancel: () => void
  onSaveDraft?: (
    newDraftSubmission: submissionService.NewDraftSubmission,
  ) => void
  onSubmit: (newFormSubmission: submissionService.NewFormSubmission) => void
  isSubmitting: boolean
  cancelError: boolean
  isLoadingAutoSaveSubmission: boolean
  isSavingDraft: boolean
  isAutoSaveSubmissionAvailable: boolean
  startNewSubmission: () => void
  continueAutoSaveSubmission: () => void
  buttons: Record<string, unknown> | undefined
  executedLookups: ExecutedLookups
  serverValidationMessage: string | undefined
  clearServerValidationMessage: () => void
}

export default function AutoSaveApprovalControlledForm({
  definition,
  submission,
  setFormSubmission,
  onCancel,
  onSaveDraft,
  onSubmit,
  isSubmitting,
  cancelError,
  isLoadingAutoSaveSubmission,
  isSavingDraft,
  isAutoSaveSubmissionAvailable,
  startNewSubmission,
  continueAutoSaveSubmission,
  buttons,
  executedLookups,
  serverValidationMessage,
  clearServerValidationMessage,
}: Props) {
  const { recaptchaKeyType, recaptchaPublicKey } = useAppConfig()
  const onBeforeUnload = useGoogleAnalyticsFormBeforeUnload({
    form: definition,
    isSubmitting,
  })

  if (cancelError) {
    return (
      <ErrorMessage>
        The form has been cancelled. Please close your browser window.
      </ErrorMessage>
    )
  }

  return (
    <>
      <OneBlinkFormControlled
        audience="SUBMITTER"
        definition={definition}
        submission={submission}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onSaveDraft={onSaveDraft}
        onBeforeUnload={onBeforeUnload}
        googleMapsApiKey={config.GOOGLE_MAP_API_KEY}
        captchaType={recaptchaKeyType}
        captchaSiteKey={recaptchaPublicKey}
        abnLookupAuthenticationGuid={config.ABN_AUTH_GUID}
        buttons={buttons}
        setFormSubmission={setFormSubmission}
        isPendingQueueEnabled={false}
        executedLookups={executedLookups}
      />
      <Dialog isOpen={isLoadingAutoSaveSubmission}>
        <LoadingSpinner>Fetching submission</LoadingSpinner>
      </Dialog>
      <Dialog isOpen={isSavingDraft}>
        <LoadingSpinner>Saving Draft</LoadingSpinner>
      </Dialog>
      <Dialog isOpen={isSubmitting}>
        <LoadingSpinner>Submitting</LoadingSpinner>
      </Dialog>
      <Dialog isOpen={isAutoSaveSubmissionAvailable}>
        <DialogHeading>
          <DialogHeadingText>Continue?</DialogHeadingText>
        </DialogHeading>
        <p>
          We found an in progress submission, would you like to pick up where
          you left off or start again?
        </p>
        <ActionsContainer>
          <button
            type="button"
            className="button ob-button is-light"
            onClick={startNewSubmission}
          >
            Start Again
          </button>
          <button
            type="button"
            className="button ob-button is-primary"
            onClick={continueAutoSaveSubmission}
          >
            Continue
          </button>
        </ActionsContainer>
      </Dialog>
      <Dialog isOpen={!!serverValidationMessage}>
        <DialogHeading>
          <DialogHeadingText>We could not submit your form</DialogHeadingText>
          <CloseIcon onClick={clearServerValidationMessage} />
        </DialogHeading>
        <ServerValidationMessage>
          {serverValidationMessage}
        </ServerValidationMessage>
      </Dialog>
      <PaymentDialog />
    </>
  )
}
